<template>
  <transition name="palette-fade">
    <div class="palette-scrim" v-if="!isInactive">
      <div class="palette-card" role="dialog" aria-label="Layout switcher">
        <div class="query-row">
          <svg class="query-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <h1 class="query-value" v-if="input">{{ input }}<span class="query-caret" /></h1>
          <span class="query-placeholder" v-else>Type a layout ID</span>
          <span class="query-hint">
            <template v-if="autoApplyCountdown !== null">Applying in {{ autoApplyCountdown }}s</template>
            <template v-else>Tab to cycle</template>
          </span>
        </div>

        <div class="palette-body" v-show="showSuggestions">
          <div class="section-label">Layouts</div>
          <ul ref="listRef" class="layout-list">
            <li
              v-for="(option, index) in filteredOptions"
              :key="option.id"
              :ref="(el) => setItemRef(el, index)"
              class="layout-item"
              :class="{ selected: index === selectedIndex }">
              <span class="layout-id">{{ option.name }}</span>
              <span class="layout-now" v-if="isCurrent(option)">now</span>
            </li>
          </ul>
        </div>

        <div class="palette-legend">
          <span><kbd>↑↓</kbd><kbd>Tab</kbd> move</span>
          <span><kbd>⏎</kbd> switch</span>
          <span><kbd>esc</kbd> dismiss</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import {
  ComponentPublicInstance,
  computed,
  ComputedRef,
  nextTick,
  onBeforeUnmount,
  onBeforeUpdate,
  onMounted,
  ref,
  watch
} from "vue";
import useStore from "../store.ts";

type LayoutOption = {
  id: number;
  name: string;
};

const input = ref("");
const selectedIndex = ref(0);
const store = useStore();
const listRef = ref<HTMLElement | null>(null);
const itemRefs = ref<HTMLElement[] | Element[] | ComponentPublicInstance[]>([]);

const options: ComputedRef<LayoutOption[]> = computed(() => store.device_settings?.allowed_pages.map((el) => {
  return {name: el, id: Number(el)} as LayoutOption;
}).filter((el: LayoutOption) => !isNaN(el.id)) ?? []);

const OFFLINE_PAGE = import.meta.env.VITE_OFFLINE_PAGE || "0";
const AUTO_APPLY_MS = 4000;
let keyDebounce: NodeJS.Timeout | null = null;
let countdownInterval: NodeJS.Timeout | null = null;
const autoApplyCountdown = ref<number | null>(null);

const filteredOptions = computed(() => {
  const query = input.value.trim().toLowerCase();
  if (!query) {
    return options.value;
  }
  return options.value.filter((option) => {
    const nameMatch = option.name.toLowerCase().includes(query);
    const idMatch = `${option.id}`.includes(query);
    return nameMatch || idMatch;
  }).sort((a, b) => Number(a.name) - Number(b.name));
});

const isInactive = computed(() => input.value.trim() === "");

const isCurrent = (option: LayoutOption) => `${store.layout}` === `${option.id}`;

const clearAutoApply = () => {
  if (keyDebounce != null) {
    clearTimeout(keyDebounce);
    keyDebounce = null;
  }
  if (countdownInterval != null) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  autoApplyCountdown.value = null;
};

const setItemRef = (el: HTMLElement | Element | ComponentPublicInstance | null, index: number) => {
  if (!el) return;
  itemRefs.value[index] = el;
};

const scrollSelectedIntoView = () => {
  const list = listRef.value;
  const item = itemRefs.value[selectedIndex.value];
  if (!list || !item) return;
  const listRect = list.getBoundingClientRect();
  const itemRect = (item as any).getBoundingClientRect() || null;
  if (itemRect == null) return;

  if (itemRect.top < listRect.top) {
    list.scrollTop -= listRect.top - itemRect.top + 8;
  } else if (itemRect.bottom > listRect.bottom) {
    list.scrollTop += itemRect.bottom - listRect.bottom + 8;
  }
};

onBeforeUpdate(() => {
  itemRefs.value = [];
});

watch(filteredOptions, (next) => {
  if (next.length === 0) {
    selectedIndex.value = 0;
    return;
  }
  if (selectedIndex.value > next.length - 1) {
    selectedIndex.value = next.length - 1;
  }
  nextTick(scrollSelectedIntoView);
});

const showSuggestions = computed(() => {
  return input.value.trim() !== "" && filteredOptions.value.length > 0;
});

const resetInputState = () => {
  input.value = "";
  selectedIndex.value = 0;
  clearAutoApply();
};

const resolveInputToOption = () => {
  const raw = input.value.trim();
  if (!raw) return null;
  const numeric = Number(raw);
  if (!Number.isNaN(numeric)) {
    return options.value.find((option) => option.id === numeric) ?? null;
  }
  const lower = raw.toLowerCase();
  const exactMatch = options.value.find((option) => option.name.toLowerCase() === lower);
  if (exactMatch) return exactMatch;
  if (filteredOptions.value.length === 1) {
    return filteredOptions.value[0];
  }
  return null;
};

const applyLayout = (layoutId: number) => {
  if (`${store.layout}` === `${layoutId}`) {
    resetInputState();
    return;
  }
  if (!store.connected && `${layoutId}` !== OFFLINE_PAGE) {
    resetInputState();
    return;
  }
  store.layout = layoutId;
  resetInputState();
};

const isDigitOnly = (value: string) => /^\d+$/.test(value);

// see docs/EXTRACTED_COMMENTS.md#c351
const commitDigitInput = () => {
  if (!isDigitOnly(input.value)) return;
  if (!resolveInputToOption()) {
    input.value = "";
    selectedIndex.value = 0;
    return;
  }
  applyLayout(Number(input.value));
};

// see docs/EXTRACTED_COMMENTS.md#c352
const armAutoApply = () => {
  clearAutoApply();
  if (!isDigitOnly(input.value)) return;
  autoApplyCountdown.value = Math.ceil(AUTO_APPLY_MS / 1000);
  countdownInterval = setInterval(() => {
    if (autoApplyCountdown.value == null) return;
    autoApplyCountdown.value -= 1;
    if (autoApplyCountdown.value <= 0 && countdownInterval != null) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
  }, 1000);
  keyDebounce = setTimeout(() => {
    clearAutoApply();
    commitDigitInput();
  }, AUTO_APPLY_MS);
};

const handleCommand = () => {
  if (input.value === "K_C") {
    console.log("[Keyboard] Received KILL_CACHE command.");
    if ((window as any).KILL_CACHE) {
      (window as any).KILL_CACHE();
      store.error = "Clearing cache...";
    } else {
      console.warn("[Keyboard] KILL_CACHE command is not available.");
    }
    setTimeout(() => {
      resetInputState();
      store.error = "";
    }, 1000);
    return true;
  }
  return false;
};

const handleTabCompletion = (event: KeyboardEvent) => {
  if (filteredOptions.value.length === 0) return;
  event.preventDefault();
  const lastIndex = filteredOptions.value.length - 1;
  if (event.shiftKey) {
    selectedIndex.value = selectedIndex.value <= 0 ? lastIndex : selectedIndex.value - 1;
  } else {
    selectedIndex.value = selectedIndex.value >= lastIndex ? 0 : selectedIndex.value + 1;
  }
  nextTick(scrollSelectedIntoView);
};

const handleKey = async (event: KeyboardEvent) => {
  if (event.target != null && (event.target as HTMLElement).nodeName !== "BODY") {
    return;
  }

  const key = event.key;

  clearAutoApply();

  if (store.ui.settingsOpen) {
    if (key === "Escape" || key === "F10") {
      store.ui.settingsOpen = false;
    }
    return;
  }

  if (key === "Escape") {
    resetInputState();
    return;
  }

  if (key === "F10") {
    store.ui.settingsOpen = true;
    resetInputState();
    return;
  }

  if (key === "F3") {
    store.ui.statusBarVisible = !store.ui.statusBarVisible;
    resetInputState();
    return;
  }

  if (key === "PageUp") {
    store.socketManager.requestPageUp();
    return;
  }

  if (key === "PageDown") {
    store.socketManager.requestPageDown();
    return;
  }

  if (key === "Tab") {
    handleTabCompletion(event);
    armAutoApply();
    return;
  }

  if (key === "Enter" || key === "XF86Red") {
    if (handleCommand()) return;
    if (input.value == "") return;

    const option = selectedIndex.value > 0 ? filteredOptions.value[selectedIndex.value] ?? null
        : (resolveInputToOption() ?? filteredOptions.value[selectedIndex.value] ?? null);
    if (!option) {
      resetInputState();
      return;
    }
    applyLayout(option.id);
    return;
  }
  if (key == "=" || event.code === "NumpadAdd") {
    if (input.value != "") return;
    if (store.cluster.enabled) return;
    if (store.socketManager.layoutManager == null) return;
    if (store.socketManager.layoutManager.getRowsUsed() == null) return;
    /* see docs/EXTRACTED_COMMENTS.md#c752 */
    const strideUp = store.socketManager.layoutManager.getRowsUsed()!.start_row;
    if (strideUp <= 0) {
      console.warn(`[Keyboard] cannot page forward: no row stride known yet (rows used = ${strideUp})`);
      return;
    }
    store.cluster.row_start = (store.cluster.row_start ?? 0) + strideUp;
    store.cluster.channel = store.socketManager.layoutManager.getRowsUsed()!.channel;

    await store.socketManager.layoutManager.updateRowStart();
    console.log(`[Keyboard] Cluster datasheet will now start @ ${store.cluster.row_start}`);
    return
  }

  if (key == "-") {
    if (input.value != "") return;
    if (store.cluster.enabled) return;
    if (store.socketManager.layoutManager == null) return;
    if (store.socketManager.layoutManager.getRowsUsed() == null) return;
    /* see docs/EXTRACTED_COMMENTS.md#c752 */
    const strideDown = store.socketManager.layoutManager.getRowsUsed()!.start_row;
    if (strideDown <= 0) {
      console.warn(`[Keyboard] cannot page back: no row stride known yet (rows used = ${strideDown})`);
      return;
    }
    store.cluster.row_start = Math.max(0, (store.cluster.row_start ?? 0) - strideDown);
    store.cluster.channel = store.socketManager.layoutManager.getRowsUsed()!.channel;
    await store.socketManager.layoutManager.updateRowStart();
    console.log(`[Keyboard] Cluster datasheet will now start @  ${store.cluster.row_start}`);
    return
  }

  if (key === "Backspace") {
    if (input.value === "") return;
    input.value = input.value.slice(0, -1);
    selectedIndex.value = 0;
    armAutoApply();
    return;
  }

  if (key.length > 1) {
    armAutoApply();
    return;
  }

  if (input.value.length > 5) return;
  input.value += key;
  selectedIndex.value = 0;
  armAutoApply();
};

onMounted(() => {
  window.addEventListener("keydown", handleKey);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKey);
  clearAutoApply();
});
</script>

<style scoped>
/* see docs/EXTRACTED_COMMENTS.md#c305 */
.palette-scrim {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 2, 37, 0.12);
}

.palette-card {
  width: min(440px, calc(100vw - 48px));
  display: flex;
  flex-direction: column;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

/* see docs/EXTRACTED_COMMENTS.md#c306 */
.query-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--border);
}

.query-glyph {
  flex: none;
  width: 20px;
  height: 20px;
  color: var(--ink-4);
}

.query-value {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.01em;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.query-caret {
  display: inline-block;
  width: 2px;
  height: 1.1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: var(--primary);
  animation: caret-blink 1.1s steps(1) infinite;
}

.query-placeholder {
  flex: 1;
  min-width: 0;
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--ink-4);
}

.query-hint {
  flex: none;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}

/* see docs/EXTRACTED_COMMENTS.md#c307 */
.palette-body {
  padding: 0;
}

.section-label {
  padding: 11px 16px 5px;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-4);
}

.layout-list {
  list-style: none;
  margin: 0;
  padding: 0 8px 8px;
  display: grid;
  gap: 1px;
  max-height: 260px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--input) transparent;
}

.layout-list::-webkit-scrollbar {
  width: 8px;
}

.layout-list::-webkit-scrollbar-thumb {
  background: var(--input);
  border-radius: 999px;
}

.layout-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  border-radius: var(--r-sm);
  position: relative;
  transition: background-color 150ms var(--ease);
}

.layout-item.selected {
  background: var(--muted);
}

.layout-item.selected::before {
  content: "";
  position: absolute;
  left: 0;
  top: 7px;
  bottom: 7px;
  width: 2px;
  border-radius: 999px;
  background: var(--primary);
}

.layout-id {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.layout-now {
  margin-left: auto;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--primary);
  background: rgba(37, 99, 235, 0.09);
  border-radius: 999px;
  padding: 2px 7px;
}

/* Footer legend — make the keyboard affordances visible. */
.palette-legend {
  display: flex;
  gap: 18px;
  padding: 12px 18px;
  border-top: 1px solid var(--border);
  background: var(--muted);
  font-size: 0.75rem;
  color: var(--ink-4);
}

.palette-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.palette-legend kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  font-family: var(--ui);
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--ink-3);
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
}

/* A single ≤150ms fade-in on summon. */
.palette-fade-enter-active,
.palette-fade-leave-active {
  transition: opacity 150ms var(--ease);
}

.palette-fade-enter-from,
.palette-fade-leave-to {
  opacity: 0;
}

@keyframes caret-blink {
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .palette-fade-enter-active,
  .palette-fade-leave-active {
    transition: none !important;
  }

  .query-caret {
    animation: none !important;
  }
}
</style>